import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, AlertTriangle, Bell, Info, X } from "lucide-react";

import { getProjectPhases } from "./project-utilisation-overview.tsx";
import type { ProjectTrackingReport } from "./types.ts";
import styles from "./workstream-tracking-page.module.css";

type Props = { project: ProjectTrackingReport };
type AlertTone = "danger" | "warning" | "info";
type ProjectAlert = { id: string; tone: AlertTone; icon: ReactNode; title: string; detail: string };
const number = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });

export function ProjectAlerts({ project }: Props) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [headerActions, setHeaderActions] = useState<HTMLElement | null>(null);
  const alerts = useMemo<ProjectAlert[]>(() => {
    const result: ProjectAlert[] = [];
    getProjectPhases(project).forEach((phase) => {
      const utilised = Number(phase.utilised ?? 0);
      const usage = phase.planned > 0 ? (utilised / phase.planned) * 100 : 0;
      if (phase.hasHourPlan && phase.planned > 0 && usage > 100) result.push({
        id: `phase-${phase.key}`,
        tone: "danger",
        icon: <AlertCircle />,
        title: `${phase.name} is over allocation`,
        detail: `${number.format(utilised)}h consumed against a ${number.format(phase.planned)}h plan — ${number.format(usage)}% utilised. Review resource assignment immediately.`,
      });
    });
    if (project.commercialRatesComplete === false || project.internalRatesComplete === false) result.push({
      id: "rates-incomplete",
      tone: "warning",
      icon: <AlertTriangle />,
      title: "Designation rates are incomplete",
      detail: "Burn and margin figures cannot be calculated reliably until all designation rates are configured in the project settings.",
    });
    const outsideHours = Number(project.unassignedPhaseHours ?? 0) + Number(project.untrackablePhaseHours ?? 0);
    if (outsideHours > 0) result.push({
      id: "outside-phases",
      tone: "info",
      icon: <Info />,
      title: `${number.format(outsideHours)}h approved outside tracked phases`,
      detail: "These hours have been approved but are not included in utilisation figures. They are tracked separately.",
    });
    return result;
  }, [project]);
  const visibleAlerts = alerts.filter((alert) => !dismissed.has(alert.id));

  useEffect(() => {
    setHeaderActions(document.getElementById("page-header-actions"));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  if (!visibleAlerts.length && !open) return null;
  const trigger = <button type="button" className={styles.projectAlertsTrigger} onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open}>
    <Bell /><span>{visibleAlerts.length} {visibleAlerts.length === 1 ? "alert" : "alerts"}</span><b>{visibleAlerts.length}</b>
  </button>;
  return <>
    {headerActions && createPortal(trigger, headerActions)}
    {open && <div className={styles.projectAlertsOverlay} role="presentation" onMouseDown={() => setOpen(false)}>
      <aside className={styles.projectAlertsDrawer} role="dialog" aria-modal="true" aria-labelledby="project-alerts-title" onMouseDown={(event) => event.stopPropagation()}>
        <header><div><h2 id="project-alerts-title">Project Alerts</h2><p>{visibleAlerts.length} active · dismiss to acknowledge</p></div><button type="button" onClick={() => setOpen(false)} aria-label="Close project alerts"><X /></button></header>
        <div className={styles.projectAlertsList}>{visibleAlerts.length ? visibleAlerts.map((alert) => <article className={`${styles.projectAlert} ${styles[`projectAlert${alert.tone}`]}`} key={alert.id}>
          <span>{alert.icon}</span><div><h3>{alert.title}</h3><p>{alert.detail}</p></div><button type="button" onClick={() => setDismissed((current) => new Set(current).add(alert.id))} aria-label={`Dismiss ${alert.title}`}><X /></button>
        </article>) : <div className={styles.projectAlertsEmpty}>All project alerts have been acknowledged.</div>}</div>
        <footer><button type="button" disabled={!visibleAlerts.length} onClick={() => setDismissed(new Set(alerts.map((alert) => alert.id)))}>Dismiss all</button></footer>
      </aside>
    </div>}
  </>;
}
